/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Virtuoso } from 'react-virtuoso'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import {
  exploreTablePeriodAtom,
  reatomExploreTableCooking,
  reatomExploreTableNew,
  reatomExploreTableTrending,
} from '@root/features/explore/atoms/explore-table'
import { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'
import {
  InfoMessage,
  mediaMaxMobile,
  SearchLoupeIcon,
} from '@root/shared/ui-kit'

import { useExploreTableTab } from '../../../hooks/use-explore-tab'

import { ExploreListItem } from './explore-item'

import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type ExploreTableProps = {
  disableScroll?: boolean
  onScroll?: (event: React.UIEvent<HTMLDivElement, UIEvent>) => void
} & ClassNameProps

export const ExploreList: React.FC<ExploreTableProps> = ({
  className,

  onScroll,
  disableScroll = false,
}) => {
  const { t } = useTranslation()
  const [tab] = useExploreTableTab()

  const trendingTableModel = useMemo(() => reatomExploreTableTrending(), [])
  const cookingTableModel = useMemo(() => reatomExploreTableCooking(), [])
  const newTableModel = useMemo(() => reatomExploreTableNew(), [])
  const ctx = useCtx()

  const model = useMemo(() => {
    return match(tab)
      .with(ExploreFeedType.Trending, () => trendingTableModel)
      .with(ExploreFeedType.Cooking, () => cookingTableModel)
      .with(ExploreFeedType.New, () => newTableModel)
      .exhaustive()
  }, [cookingTableModel, newTableModel, tab, trendingTableModel])

  useEffect(() => {
    const subs = [
      trendingTableModel.initialize(ctx),
      cookingTableModel.initialize(ctx),
      newTableModel.initialize(ctx),
    ]

    return () => {
      for (const unsub of subs) {
        unsub()
      }
    }
  }, [cookingTableModel, ctx, newTableModel, trendingTableModel])

  const [period] = useAtom(exploreTablePeriodAtom)

  const [itemsState] = useAtom(model)
  const [loadMoreState] = useAtom(model.loadMoreState)
  const loadMore = useAction(model.loadMore)

  const ctxValue = useMemo(() => ({ period, feedType: tab }), [period, tab])

  return (
    <Root className={className}>
      <List
        $disabledScroll={disableScroll}
        onScroll={onScroll}
        data={itemsState.value?.data ?? []}
        endReached={() => {
          if (AsyncStateN.isError(loadMoreState)) {
            return
          }

          loadMore()
        }}
        components={{
          Footer: () => {
            if (AsyncStateN.isError(loadMoreState)) {
              return (
                <InfoMessage
                  intent="error"
                  variant="dt"
                  onRetry={loadMore}
                  withSeparator
                />
              )
            }
          },
          EmptyPlaceholder: () => (
            <Info>
              <InfoMessage
                intent="empty"
                variant="dt"
                title={t('common.empty-unfortunately')}
                description={t('common.no-items-found')}
                icon={<SearchLoupeIcon />}
                withBackground
              />
            </Info>
          ),
        }}
        itemContent={(index, value) => (
          <ExploreListItem
            context={ctxValue}
            index={index}
            model={model}
            pair={value}
          />
        )}
      />
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
`

const List = styled(Virtuoso<PairModel, unknown>)<{
  $disabledScroll: boolean
}>`
  & > div {
    ${(props) =>
      props.$disabledScroll
        ? css`
            overflow: hidden;
          `
        : ''};
  }

  ${mediaMaxMobile(css`
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar {
      display: none; // Safari and Chrome
    }
  `)}
`

const Info = styled.div`
  display: grid;
  height: 100%;
`
